<template>
  <nav aria-label="Checkout progress" class="w-full">
    <ol class="flex items-center">
      <li
        v-for="(step, index) in steps"
        :key="step.key"
        class="flex items-center"
        :class="{ 'flex-1': index < steps.length - 1 }"
      >
        <!-- Step circle + label -->
        <component
          :is="isClickable(index + 1) ? 'button' : 'div'"
          class="flex flex-col items-center"
          :class="{ 'cursor-pointer': isClickable(index + 1) }"
          v-bind="isClickable(index + 1) ? { type: 'button' } : {}"
          @click="onStepClick(index + 1)"
        >
          <div
            class="flex items-center justify-center w-8 h-8 rounded-full font-semibold transition-colors"
            :class="stepCircleClass(index + 1)"
          >
            <template v-if="index + 1 < currentStep">
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </template>
            <template v-else>
              {{ index + 1 }}
            </template>
          </div>
          <span
            class="mt-1.5 font-medium whitespace-nowrap"
            :class="stepLabelClass(index + 1)"
          >
            {{ t(step.labelKey) }}
          </span>
        </component>

        <!-- Connector line -->
        <div
          v-if="index < steps.length - 1"
          class="flex-1 h-0.5 mx-3 -mt-5 transition-colors"
          :class="stepLineClass(index + 1)"
        ></div>
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
const { t } = useI18n();

const props = defineProps<{
  currentStep: number;
}>();

const emit = defineEmits<{
  (e: 'navigate', step: number): void;
}>();

type StepDefinition = {
  key: string;
  labelKey: string;
};

const steps: StepDefinition[] = [
  { key: 'configure', labelKey: 'checkout/stepper/configure' },
  { key: 'signin', labelKey: 'checkout/stepper/signin' },
  { key: 'verify', labelKey: 'checkout/stepper/verify' },
  { key: 'checkout', labelKey: 'checkout/stepper/checkout' },
];

function isClickable(step: number): boolean {
  return step < props.currentStep;
}

function onStepClick(step: number): void {
  if (isClickable(step)) {
    emit('navigate', step);
  }
}

function stepCircleClass(step: number): string {
  if (step < props.currentStep) {
    return 'bg-success text-onyx hover:bg-success/80';
  }
  if (step === props.currentStep) {
    return 'bg-brand text-onyx';
  }
  return 'bg-depth-2 text-mouse';
}

function stepLabelClass(step: number): string {
  if (step < props.currentStep) {
    return 'text-success';
  }
  if (step === props.currentStep) {
    return 'text-brand';
  }
  return 'text-mouse';
}

function stepLineClass(step: number): string {
  if (step < props.currentStep) {
    return 'bg-success';
  }
  return 'bg-depth-2';
}
</script>
